import React, { useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Users,
  Wallet,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  UserPlus,
  RefreshCw,
  CheckCircle,
  Clock,
  XCircle,
} from 'lucide-react';
import api from '../utils/axios';
import formatCurrency from '../helper/formatCurrency';

/* ── Design tokens ── */
const G = '#1C8C5B';
const GBORDER = '#E8EBF4';
const BG = '#F4F6FC';
const CARD = '#FFFFFF';
const TEXT = '#111111';
const MUTED = '#6D7588';

/* ── Helpers ── */
const formatNumber = (v?: number) => Number(v ?? 0).toFixed(2);
const pct = (v?: number) => Math.min(Math.max(Number(v ?? 0), 0), 100);

/* ─────────────────────────────────────────────
   Radial Gauge component (ApexCharts)
───────────────────────────────────────────── */
const GaugeChart = ({
  value,
  color,
  label,
}: {
  value: number;
  color: string;
  label: string;
}) => {
  const opts: ApexCharts.ApexOptions = {
    chart: { type: 'radialBar', sparkline: { enabled: true } },
    plotOptions: {
      radialBar: {
        startAngle: -90,
        endAngle: 90,
        track: { background: '#F0F0F0', strokeWidth: '97%' },
        dataLabels: { show: false },
        hollow: { size: '55%' },
      },
    },
    fill: { colors: [color] },
    stroke: { lineCap: 'round' },
    labels: [label],
  };
  return (
    <ReactApexChart
      type="radialBar"
      series={[pct(value)]}
      options={opts}
      height={80}
      width={80}
    />
  );
};

/* ─────────────────────────────────────────────
   Sparkline line chart (7-day trend)
───────────────────────────────────────────── */
const TrendLine = ({
  data,
  color,
}: {
  data: number[];
  color: string;
}) => {
  const opts: ApexCharts.ApexOptions = {
    chart: { type: 'line', sparkline: { enabled: true }, animations: { enabled: true } },
    stroke: { curve: 'smooth', width: 2 },
    colors: [color],
    tooltip: { enabled: false },
  };
  return (
    <ReactApexChart
      type="line"
      series={[{ data }]}
      options={opts}
      height={50}
      width={80}
    />
  );
};

/* ─────────────────────────────────────────────
   Big area line chart (7-day payout trend)
───────────────────────────────────────────── */
const BigLineChart = () => {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const data = [210000, 310000, 380000, 290000, 420000, 350000, 270000];

  const opts: ApexCharts.ApexOptions = {
    chart: { type: 'area', toolbar: { show: false }, zoom: { enabled: false } },
    stroke: { curve: 'smooth', width: 2, colors: [G] },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.25,
        opacityTo: 0.01,
        stops: [0, 100],
        colorStops: [{ offset: 0, color: G, opacity: 0.25 }, { offset: 100, color: G, opacity: 0 }],
      },
    },
    markers: { size: 4, colors: [G], strokeColors: '#fff', strokeWidth: 2 },
    xaxis: {
      categories: days,
      labels: { style: { fontSize: '12px', fontFamily: 'Inter', colors: MUTED } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { fontSize: '11px', fontFamily: 'Inter', colors: MUTED },
        formatter: (v: number) => `৳${(v / 1000).toFixed(0)}k`,
      },
    },
    grid: { borderColor: GBORDER, strokeDashArray: 4 },
    tooltip: {
      style: { fontFamily: 'Inter' },
      y: { formatter: (v: number) => `৳${v.toLocaleString()}` },
    },
    dataLabels: { enabled: false },
  };

  return (
    <ReactApexChart
      type="area"
      series={[{ name: 'Deposits', data }]}
      options={opts}
      height={210}
    />
  );
};


/* ═══════════════════════════════════════════
   Main Dashboard
═══════════════════════════════════════════ */
const AdminDashboard = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/getDashboardData');
      setData(res.data?.data ?? {});
      setLastUpdated(new Date());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 16, marginBottom: 16,
        }}>
          {[...Array(4)].map((_, i) => (
            <div key={i} style={{
              height: 110, background: CARD, borderRadius: 14,
              border: `1px solid ${GBORDER}`,
              animation: 'pulse 1.5s infinite',
            }} />
          ))}
        </div>
        <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}`}</style>
      </div>
    );
  }

  const deposits = data?.deposits ?? {};
  const withdrawals = data?.withdrawals ?? {};

  /* tiny fake spark data for cards */
  const upSpark = [10, 15, 12, 18, 14, 20, 22];
  const downSpark = [22, 20, 18, 15, 12, 10, 8];

  return (
    <div style={{ minHeight: '100vh', fontFamily: "'Inter', sans-serif" }} className="dash-root">

      {/* ── Page Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: TEXT, margin: 0 }}>Dashboard</h1>
          <p style={{ fontSize: 13, color: MUTED, margin: '4px 0 0' }}>
            Welcome back! Here's what's happening today.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            fontSize: 12, color: MUTED, background: CARD,
            border: `1px solid ${GBORDER}`, borderRadius: 8,
            padding: '6px 12px',
          }}>
            Updated: {lastUpdated.toLocaleTimeString()}
          </span>
          <button
            onClick={fetchData}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '7px 16px', borderRadius: 8,
              background: TEXT, color: '#fff',
              fontSize: 13, fontWeight: 600,
              border: 'none', cursor: 'pointer',
            }}
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* ── Row 1: 4 Top Stat Cards ── */}
      <div className="dash-grid-4" style={{ marginBottom: 16 }}>
        <StatCard
          label="TOTAL USERS"
          value={String(data?.totalUsers?.toLocaleString() ?? '0')}
          sub="+12% vs last month"
          trend="up"
          sparkData={upSpark}
          gaugeVal={72}
          gaugeColor={G}
        />
        <StatCard
          label="TOTAL DEPOSITS"
          value={formatCurrency(data?.totalDeposits)}
          sub="+20.1% vs last month"
          trend="up"
          sparkData={upSpark}
          gaugeVal={60}
          gaugeColor="#3B82F6"
        />
        <StatCard
          label="TOTAL WITHDRAWALS"
          value={formatCurrency(data?.totalWithdrawals)}
          sub="-5% vs last month"
          trend="down"
          sparkData={downSpark}
          gaugeVal={38}
          gaugeColor="#EF4444"
        />
        <StatCard
          label="NET REVENUE"
          value={formatCurrency((data?.totalDeposits ?? 0) - (data?.totalWithdrawals ?? 0))}
          sub="Deposits − Withdrawals"
          trend="up"
          sparkData={upSpark}
          gaugeVal={55}
          gaugeColor="#F59E0B"
        />
      </div>

      {/* ── Row 2: Wallet Balances ── */}
      <div className="dash-grid-2" style={{ marginBottom: 16 }}>
        <WalletCard
          title="Total Client Wallet Balance"
          value={formatCurrency(data?.totalClientWalletBalance)}
          change="+20.1% from last month"
          positive
          gradient="linear-gradient(135deg, #1C8C5B 0%, #10B981 100%)"
        />
        <WalletCard
          title="Total Agent Wallet Balance"
          value={formatCurrency(data?.totalAgentWalletBalance)}
          change="+15% from last month"
          positive
          gradient="linear-gradient(135deg, #3B82F6 0%, #06B6D4 100%)"
        />
      </div>

      {/* ── Row 3: Chart + Overview cards ── */}
      <div className="dash-grid-2" style={{ marginBottom: 16 }}>

        {/* 7-Day Trend */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
            <div>
              <p style={{ fontSize: 15, fontWeight: 700, color: TEXT, margin: 0 }}>7-Day Deposit Trend</p>
              <p style={{ fontSize: 12, color: MUTED, margin: '3px 0 0' }}>Simulated weekly overview</p>
            </div>
            <p style={{ fontSize: 18, fontWeight: 700, color: TEXT, margin: 0 }}>
              {formatCurrency(data?.totalDeposits)}<br />
              <span style={{ fontSize: 11, color: MUTED, fontWeight: 400 }}>Total</span>
            </p>
          </div>
          <BigLineChart />
        </div>

        {/* Overview columns */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <OverviewCard title="Deposits Overview" color={G} stats={[
            { label: 'Approved', count: deposits.approvedCount, amount: deposits.approvedAmount, status: 'ok' },
            { label: 'Pending', count: deposits.pendingCount, amount: deposits.pendingAmount, status: 'warn' },
            { label: 'Rejected', count: deposits.rejectedCount, amount: deposits.rejectedAmount, status: 'bad' },
          ]} />
          <OverviewCard title="Withdrawals Overview" color="#EF4444" stats={[
            { label: 'Approved', count: withdrawals.approvedCount, amount: withdrawals.approvedAmount, status: 'ok' },
            { label: 'Pending', count: withdrawals.pendingCount, amount: withdrawals.pendingAmount, status: 'warn' },
            { label: 'Rejected', count: withdrawals.rejectedCount, amount: withdrawals.rejectedAmount, status: 'bad' },
          ]} />
        </div>
      </div>

      {/* ── Row 4: Today's Activity ── */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'linear-gradient(135deg, #6D7588 0%, #444 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Activity size={18} color="#fff" />
          </div>
          <div>
            <p style={{ fontSize: 15, fontWeight: 700, color: TEXT, margin: 0 }}>Today's Activity</p>
            <p style={{ fontSize: 12, color: MUTED, margin: 0 }}>Real-time metrics for today</p>
          </div>
        </div>
        <div className="dash-grid-4-sm">
          <TodayCard label="Today's Deposits" value={formatCurrency(data?.todayDeposits)} icon={<ArrowUpCircle size={18} />} color={G} />
          <TodayCard label="Today's Withdrawals" value={formatCurrency(data?.todayWithdrawals)} icon={<ArrowDownCircle size={18} />} color="#EF4444" />
          <TodayCard label="New Users Today" value={String(data?.todayUsers ?? 0)} icon={<UserPlus size={18} />} color="#3B82F6" />
          <TodayCard label="Today's Net"
            value={formatCurrency((data?.todayDeposits ?? 0) - (data?.todayWithdrawals ?? 0))}
            icon={<DollarSign size={18} />} color="#F59E0B"
          />
        </div>
      </div>

      {/* ── Row 5: Summary cards ── */}
      <div className="dash-grid-3">
        <SummaryCard
          title="Avg. Deposit per User"
          value={formatCurrency((data?.totalDeposits ?? 0) / (data?.totalUsers || 1))}
          icon={<TrendingUp size={18} />}
        />
        <SummaryCard
          title="Avg. Withdrawal per User"
          value={formatCurrency((data?.totalWithdrawals ?? 0) / (data?.totalUsers || 1))}
          icon={<TrendingDown size={18} />}
        />
        <SummaryCard
          title="Total Platform Balance"
          value={formatCurrency((data?.totalClientWalletBalance ?? 0) + (data?.totalAgentWalletBalance ?? 0))}
          icon={<Wallet size={18} />}
        />
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        // .dash-root { padding: 20px 24px; }

        /* 4-column grid → 2 on tablet → 1 on mobile */
        .dash-grid-4 {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 14px;
        }

        /* 4-col → 2-col at ≤640px (Today's activity) */
        .dash-grid-4-sm {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }

        /* 2-column grid → 1 on mobile */
        .dash-grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }

        /* 3-column grid → 2 on tablet → 1 on mobile */
        .dash-grid-3 {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 14px;
        }

        /* Tablet: ≤900px */
        @media (max-width: 900px) {
          .dash-grid-4   { grid-template-columns: repeat(2, 1fr); }
          .dash-grid-4-sm { grid-template-columns: repeat(2, 1fr); }
          .dash-grid-3   { grid-template-columns: repeat(2, 1fr); }
        }

        /* Mobile: ≤600px */
        @media (max-width: 600px) {
          // .dash-root { padding: 12px 14px; }
          .dash-grid-4   { grid-template-columns: 1fr; }
          .dash-grid-4-sm { grid-template-columns: repeat(2, 1fr); }
          .dash-grid-2   { grid-template-columns: 1fr; }
          .dash-grid-3   { grid-template-columns: 1fr; }
        }

        /* Small mobile: ≤380px */
        @media (max-width: 380px) {
          .dash-grid-4-sm { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

/* ═══════════════════════════════════════════
   Sub-components
═══════════════════════════════════════════ */

const cardStyle: React.CSSProperties = {
  background: CARD,
  borderRadius: 14,
  border: `1px solid ${GBORDER}`,
  padding: 20,
  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
};

/* Stat card with radial gauge */
const StatCard = ({
  label, value, sub, trend, sparkData, gaugeVal, gaugeColor,
}: {
  label: string; value: string; sub: string;
  trend: 'up' | 'down'; sparkData: number[];
  gaugeVal: number; gaugeColor: string;
}) => (
  <div style={cardStyle}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: 10, fontWeight: 600, color: MUTED, margin: 0, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          {label}
        </p>
        <p style={{ fontSize: 22, fontWeight: 700, color: TEXT, margin: '6px 0 4px', lineHeight: 1.1 }}>
          {value}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {trend === 'up'
            ? <TrendingUp size={13} color={G} />
            : <TrendingDown size={13} color="#EF4444" />}
          <span style={{ fontSize: 11, color: trend === 'up' ? G : '#EF4444', fontWeight: 500 }}>{sub}</span>
        </div>
      </div>
      {/* Gauge */}
      <div style={{ marginTop: -8, marginRight: -8 }}>
        <GaugeChart value={gaugeVal} color={gaugeColor} label={label} />
      </div>
    </div>
  </div>
);

/* Wallet balance card with gradient */
const WalletCard = ({
  title, value, change, positive, gradient,
}: {
  title: string; value: string; change: string; positive: boolean; gradient: string;
}) => (
  <div style={{
    ...cardStyle, padding: 0, overflow: 'hidden',
  }}>
    <div style={{ background: gradient, padding: '20px 22px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', margin: '0 0 6px', fontWeight: 500 }}>{title}</p>
          <p style={{ fontSize: 30, fontWeight: 700, color: '#fff', margin: 0, lineHeight: 1 }}>{value}</p>
        </div>
        <div style={{
          width: 44, height: 44, borderRadius: 12,
          background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Wallet size={22} color="#fff" />
        </div>
      </div>
    </div>
    <div style={{ padding: '12px 22px', display: 'flex', alignItems: 'center', gap: 6 }}>
      {positive
        ? <TrendingUp size={14} color={G} />
        : <TrendingDown size={14} color="#EF4444" />}
      <span style={{ fontSize: 12, color: positive ? G : '#EF4444', fontWeight: 500 }}>{change}</span>
    </div>
  </div>
);

/* Overview card (deposits / withdrawals breakdown) */
const statusIcon = (s: string) =>
  s === 'ok' ? <CheckCircle size={15} color={G} />
    : s === 'warn' ? <Clock size={15} color="#F59E0B" />
      : <XCircle size={15} color="#EF4444" />;

const statusBadge = (s: string): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 4,
  fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 999,
  background: s === 'ok' ? '#ECFDF5' : s === 'warn' ? '#FFFBEB' : '#FEF2F2',
  color: s === 'ok' ? G : s === 'warn' ? '#D97706' : '#DC2626',
});

const OverviewCard = ({
  title, color, stats,
}: {
  title: string; color: string;
  stats: { label: string; count: number; amount: number; status: string }[];
}) => (
  <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
    <div style={{ background: color, padding: '12px 18px' }}>
      <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: 0 }}>{title}</p>
    </div>
    <div style={{ padding: '10px 16px' }}>
      {stats.map((s, i) => (
        <div key={s.label} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 0',
          borderBottom: i < stats.length - 1 ? `1px solid ${GBORDER}` : 'none',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={statusBadge(s.status)}>
              {statusIcon(s.status)} {s.label}
            </span>
            <span style={{ fontSize: 11, color: MUTED }}>{s.count ?? 0} txns</span>
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: TEXT }}>
            {formatCurrency(s.amount)}
          </span>
        </div>
      ))}
    </div>
  </div>
);

/* Today's activity mini card */
const TodayCard = ({
  label, value, icon, color,
}: {
  label: string; value: string | number; icon: React.ReactNode; color: string;
}) => (
  <div style={{
    background: '#F9FAFB', borderRadius: 12,
    border: `1px solid ${GBORDER}`, padding: '14px 16px',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <div style={{
        width: 34, height: 34, borderRadius: 9,
        background: color, display: 'flex', alignItems: 'center',
        justifyContent: 'center', color: '#fff', flexShrink: 0,
        boxShadow: `0 2px 8px ${color}44`,
      }}>
        {icon}
      </div>
      <p style={{ fontSize: 11, fontWeight: 600, color: MUTED, margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </p>
    </div>
    <p style={{ fontSize: 20, fontWeight: 700, color: TEXT, margin: 0 }}>{value}</p>
  </div>
);

/* Summary card */
const SummaryCard = ({
  title, value, icon,
}: {
  title: string; value: string; icon: React.ReactNode;
}) => (
  <div style={cardStyle}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: '#F4F6FC', border: `1px solid ${GBORDER}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED,
      }}>
        {icon}
      </div>
      <p style={{ fontSize: 13, fontWeight: 500, color: MUTED, margin: 0 }}>{title}</p>
    </div>
    <p style={{ fontSize: 22, fontWeight: 700, color: TEXT, margin: 0 }}>{value}</p>
  </div>
);

export default AdminDashboard;